import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export type ProcedureStatus = "PENDIENTE" | "EN_PROCESO" | "EN_REVISION" | "APROBADO" | "FINALIZADO" | "CANCELADO";

export const PROCEDURE_STATUS_LABEL: Record<ProcedureStatus, string> = {
  PENDIENTE: "Pendiente",
  EN_PROCESO: "En proceso",
  EN_REVISION: "En revisión",
  APROBADO: "Aprobado",
  FINALIZADO: "Finalizado",
  CANCELADO: "Cancelado",
};

export interface ProcedureRecord {
  id: string;
  clientId: string;
  title: string;
  procedureType: string | null;
  entityName: string | null;
  description: string | null;
  status: ProcedureStatus;
  filingDate: string | null;
  dueDate: string | null;
  completedDate: string | null;
  cost: string | null;
  referenceNumber: string | null;
  notes: string | null;
  createdAt: string;
  client?: { id: string; fullName: string | null; businessName: string | null; tradeName: string | null; clientType: string } | null;
  assignedTo?: { id: string; fullName: string; email: string } | null;
}

export interface ListProceduresFilters {
  page?: number;
  pageSize?: number;
  clientId?: string;
  status?: ProcedureStatus;
  search?: string;
  overdue?: boolean;
}

export function useProcedures(filters: ListProceduresFilters = {}) {
  return useQuery({
    queryKey: ["procedures", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<ProcedureRecord[]>>("/procedures", { params: filters });
      return data;
    },
  });
}

export interface CreateProcedurePayload {
  clientId: string;
  title: string;
  procedureType?: string;
  entityName?: string;
  description?: string;
  status?: ProcedureStatus;
  filingDate?: string;
  dueDate?: string;
  cost?: number | string;
  referenceNumber?: string;
  notes?: string;
  assignedToUserId?: string;
}

export function useCreateProcedure() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateProcedurePayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<ProcedureRecord>>("/procedures", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["procedures"] }),
  });
}

export function useUpdateProcedure() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<CreateProcedurePayload> & { id: string }) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<ProcedureRecord>>(`/procedures/${id}`, payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["procedures"] }),
  });
}

export function useDeleteProcedure() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/procedures/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["procedures"] }),
  });
}

export function procedureClientName(procedure: ProcedureRecord): string | null {
  if (!procedure.client) return null;
  return procedure.client.clientType === "PERSONA_JURIDICA"
    ? procedure.client.businessName || procedure.client.tradeName || "Cliente"
    : procedure.client.fullName || "Cliente";
}

export function isProcedureOverdue(procedure: ProcedureRecord): boolean {
  if (!procedure.dueDate) return false;
  if (procedure.status === "FINALIZADO" || procedure.status === "CANCELADO") return false;
  return new Date(procedure.dueDate) < new Date(new Date().toDateString());
}
